import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, Mail, ReceiptText, LogOut } from "lucide-react";
import { IconButton, Text } from "@components/atoms";
import { useAuth } from "@app/providers/useAuth";
import { ROUTES } from "@shared/constants";

/**
 * User menu organism.
 * Dropdown with profile, contact, about, and logout options.
 * Shown when user is authenticated.
 */
export function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { logout } = useAuth();
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const menuItems = [
    {
      icon: <User size={18} />,
      label: "Mi perfil",
      action: () => {
        navigate(ROUTES.PROFILE);
        setIsOpen(false);
      },
    },
    {
      icon: <Mail size={18} />,
      label: "Contacto",
      action: () => {
        navigate(ROUTES.CONTACT);
        setIsOpen(false);
      },
    },
    {
      icon: <ReceiptText size={18} />,
      label: "Nosotros",
      action: () => {
        navigate(ROUTES.ABOUT);
        setIsOpen(false);
      },
    },
    {
      icon: <LogOut size={18} />,
      label: "Log out",
      action: () => {
        logout();
        setIsOpen(false);
        navigate(ROUTES.HOME);
      },
    },
  ];

  return (
    <div ref={menuRef} className="relative">
      <IconButton
        size={24}
        className="text-secondary-120"
        onClick={() => setIsOpen(!isOpen)}
      >
        <User size={24} />
      </IconButton>

      {isOpen && (
        <div
          className="absolute right-0 top-full z-30 mt-2 flex flex-col rounded-lg bg-white p-2 shadow-lg"
          style={{ width: 180, height: 200 }}
        >
          {menuItems.map((item) => (
            <button
              key={item.label}
              className="flex cursor-pointer items-center gap-3 border-none bg-transparent px-5 py-3.5 text-left transition-colors hover:bg-neutral-10"
              onClick={item.action}
            >
              <span className="text-black">{item.icon}</span>
              <Text variant="body" as="span" className="text-black">
                {item.label}
              </Text>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
